import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Link } from "react-router-dom";

const STEPS = [
  {
    id: "license",
    question: "What's your license?",
    sub: "Select the highest CDL class you currently hold.",
    options: [
      { id: "cdl-a", label: "CDL-A", desc: "Full commercial license" },
      { id: "cdl-b", label: "CDL-B", desc: "Straight trucks & some combos" },
      { id: "class-c", label: "Class C / No CDL", desc: "Non-commercial or getting started" },
    ],
  },
  {
    id: "gig",
    question: "What's your preferred setup?",
    sub: "Pick the work arrangement that fits your goals.",
    options: [
      { id: "company", label: "W2 Company Driver", desc: "Steady pay, no truck expenses" },
      { id: "lease", label: "Lease / Rent", desc: "Flexibility, path to ownership" },
      { id: "local", label: "Local / Regional", desc: "Home more often, consistent pay" },
      { id: "trainee", label: "Just Getting Started", desc: "Trainee or non-CDL positions" },
    ],
  },
  {
    id: "hometime",
    question: "How much home time do you need?",
    sub: "We'll match you to lanes that fit your life.",
    options: [
      { id: "daily",    label: "Home Daily",    desc: "Local routes, back every night" },
      { id: "weekly",   label: "Home Weekly",   desc: "Regional, 5–6 days out" },
      { id: "biweekly", label: "Bi-Weekly",     desc: "OTR with regular breaks" },
      { id: "otr",      label: "OTR / Long Haul", desc: "2+ weeks out, max miles" },
    ],
  },
];

const PACKAGES = {
  "$1 Company Team Driving (CPM)": { color: "#FFB300", badge: "Top CPM" },
  "85¢+ Company Driver (CPM)": { color: "#FFB300", badge: "OTR" },
  "30%+ Company Driver (PPL)": { color: "#FFB300", badge: "PPL" },
  "88% Lease To Own": { color: "#a3e635", badge: "Ownership" },
  "88% Rent Program": { color: "#a3e635", badge: "Flexible" },
  "$1,200-2,500 Local/Regional W2": { color: "#38bdf8", badge: "Home Weekly" },
  "$30/hr Local": { color: "#38bdf8", badge: "Home Daily" },
  "$650-950 Trainee Program (No Experience)": { color: "#c084fc", badge: "Entry Level" },
  "65¢ Box Truck (Non-CDL)": { color: "#c084fc", badge: "No CDL" },
};

function getMatches(license, gig, hometime) {
  const matches = [];
  if (license === "cdl-a") {
    if (gig === "company") {
      if (hometime === "otr" || hometime === "biweekly") {
        matches.push("$1 Company Team Driving (CPM)", "85¢+ Company Driver (CPM)", "30%+ Company Driver (PPL)");
      } else {
        matches.push("$1,200-2,500 Local/Regional W2", "$30/hr Local");
      }
    } else if (gig === "lease") {
      matches.push("88% Lease To Own", "88% Rent Program");
    } else if (gig === "local") {
      matches.push("$1,200-2,500 Local/Regional W2", "$30/hr Local");
    } else {
      matches.push("$650-950 Trainee Program (No Experience)", "85¢+ Company Driver (CPM)");
    }
  } else if (license === "cdl-b") {
    if (hometime === "daily" || hometime === "weekly") {
      matches.push("$30/hr Local", "$1,200-2,500 Local/Regional W2");
    } else {
      matches.push("$1,200-2,500 Local/Regional W2", "$650-950 Trainee Program (No Experience)");
    }
  } else {
    matches.push("65¢ Box Truck (Non-CDL)", "$650-950 Trainee Program (No Experience)");
  }
  return matches;
}

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
};

export default function FindMyLane() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [dir, setDir] = useState(1);
  const [done, setDone] = useState(false);

  const current = STEPS[step];

  const choose = (optId) => {
    const key = current.id;
    const next = { ...answers, [key]: optId };
    setAnswers(next);
    if (step < STEPS.length - 1) {
      setDir(1);
      setStep((s) => s + 1);
    } else {
      setDir(1);
      setDone(true);
    }
  };

  const back = () => {
    if (done) { setDone(false); return; }
    if (step > 0) { setDir(-1); setStep((s) => s - 1); }
  };

  const restart = () => {
    setAnswers({});
    setStep(0);
    setDone(false);
    setDir(-1);
  };

  const matches = done
    ? getMatches(answers.license, answers.gig, answers.hometime)
    : [];

  return (
    <section className="py-24 md:py-32 bg-muted" data-testid="find-my-lane">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <span className="font-head text-xs md:text-sm font-bold uppercase tracking-[0.4em] text-primary">
            Find My Lane
          </span>
          <h2 className="font-head text-3xl md:text-5xl font-medium uppercase tracking-[0.15em] mt-4 text-[#0A0A0A]">
            Your Perfect Match
          </h2>
          <p className="mt-4 text-foreground/60 max-w-xl mx-auto">
            Answer 3 quick questions. We'll show you exactly which pay package fits your life.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Progress */}
          {!done && (
            <div className="flex gap-2 mb-8">
              {STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 transition-colors duration-300 ${i <= step ? "bg-primary" : "bg-border"}`}
                />
              ))}
            </div>
          )}

          {/* Card */}
          <div className="bg-white border border-border overflow-hidden" style={{ minHeight: 360 }}>
            <AnimatePresence custom={dir} mode="wait">
              {!done ? (
                <motion.div
                  key={`step-${step}`}
                  custom={dir}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.22, ease: "easeInOut" }}
                  className="p-8 md:p-10"
                >
                  <p className="font-head text-xs uppercase tracking-[0.3em] text-primary mb-3">
                    Step {step + 1} of {STEPS.length}
                  </p>
                  <h3 className="font-head text-2xl md:text-3xl uppercase tracking-[0.1em] text-[#0A0A0A] mb-2">
                    {current.question}
                  </h3>
                  <p className="text-foreground/60 text-sm mb-8">{current.sub}</p>

                  <div className="grid sm:grid-cols-2 gap-3">
                    {current.options.map((opt) => (
                      <button
                        key={opt.id}
                        data-testid={`lane-opt-${opt.id}`}
                        type="button"
                        onClick={() => choose(opt.id)}
                        className="text-left p-5 border-2 border-border hover:border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white group transition-all duration-200"
                      >
                        <p className="font-head text-sm uppercase tracking-[0.12em] group-hover:text-white">{opt.label}</p>
                        <p className="text-xs text-foreground/60 group-hover:text-white/60 mt-1">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="result"
                  custom={dir}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.22, ease: "easeInOut" }}
                  className="p-8 md:p-10"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-black" />
                    </div>
                    <p className="font-head text-xs uppercase tracking-[0.3em] text-primary">Your Match</p>
                  </div>
                  <h3 className="font-head text-2xl md:text-3xl uppercase tracking-[0.1em] text-[#0A0A0A] mb-2">
                    Your Best Lanes
                  </h3>
                  <p className="text-foreground/60 text-sm mb-8">Based on your answers, here are the packages that fit you best.</p>

                  <div className="space-y-3 mb-8">
                    {matches.map((pkg, i) => {
                      const meta = PACKAGES[pkg] || { color: "#FFB300", badge: "" };
                      return (
                        <motion.div
                          key={pkg}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.08 }}
                          className="flex items-center justify-between border border-border px-5 py-4 bg-white"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
                            <span className="font-head text-sm uppercase tracking-[0.1em] text-[#0A0A0A]">{pkg}</span>
                          </div>
                          <span
                            className="font-head text-[10px] uppercase tracking-[0.2em] px-2.5 py-1"
                            style={{ background: meta.color + "20", color: meta.color }}
                          >
                            {meta.badge}
                          </span>
                        </motion.div>
                      );
                    })}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/apply"
                      data-testid="lane-apply-btn"
                      className="flex-1 flex items-center justify-center gap-2 font-head text-sm uppercase tracking-[0.2em] bg-primary text-black px-6 py-4 hover:bg-[#0A0A0A] hover:text-primary transition-colors duration-300"
                    >
                      Apply Now <ArrowRight className="w-4 h-4" />
                    </Link>
                    <button
                      type="button"
                      data-testid="lane-restart-btn"
                      onClick={restart}
                      className="flex-1 font-head text-sm uppercase tracking-[0.2em] border-2 border-border text-[#0A0A0A] px-6 py-4 hover:bg-[#0A0A0A] hover:text-white transition-colors"
                    >
                      Start Over
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Back / step counter */}
          {(step > 0 || done) && (
            <button
              type="button"
              onClick={back}
              data-testid="lane-back-btn"
              className="mt-4 font-head text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-[#0A0A0A] transition-colors"
            >
              ← Back
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
